import AttributionModelsIcon from '@precis-digital/kurama/icons/AttributionModelsIcon';
import BudgetOptimiserIcon from '@precis-digital/kurama/icons/BudgetOptimiserIcon';
import ConnectDataSourcesIcon from '@precis-digital/kurama/icons/ConnectDataSourcesIcon';
import CreativeReportingIcon from '@precis-digital/kurama/icons/CreativeReportingIcon';
import DocumentationIcon from '@precis-digital/kurama/icons/DocumentationIcon';
import EnhancedSignalsIcon from '@precis-digital/kurama/icons/EnhancedSignalsIcon';
import RecommendationsIcon from '@precis-digital/kurama/icons/RecommendationsIcon';
import Badge from '@precis-digital/kurama/Badge';
import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import { type ReactElement } from 'react';
import { type TFunction } from 'shared/translations';
import { type CirrusSourceGroup } from 'shared/copilot/hooks/useCopilotCirrusSourceOptions';
import { type CirrusCollection, type CirrusSourceOption } from 'shared/copilot/cirrusSource';
import { getErrorCodeDetails, STATUS_COLOR_MAP, type ConfigStatus } from 'shared/configStatuses';
import StatusTooltipBody from 'shared/components/StatusTooltipBody';
import StatusTooltipTitle from 'shared/components/StatusTooltipTitle';
import { type AlvieDatasourceOption } from './alvieDatasourceOptions';
export const COPILOT_DATA_SOURCES_MENU_VALUE = 'data-sources';
export const COPILOT_KNOWLEDGE_SOURCES_MENU_VALUE = 'knowledge-sources';

/** Product icon for a Cirrus collection — shared by the source menu and selected chips. */
export const getCirrusCollectionIcon = (collection: CirrusCollection): ReactElement => {
  switch (collection) {
    case 'event_feed':
      return <RecommendationsIcon />;
    case 'standard_reports':
      return <CreativeReportingIcon />;
    case 'attribution':
      return <AttributionModelsIcon />;
    case 'bo':
      return <BudgetOptimiserIcon />;
    case 'enhanced_signals':
      return <EnhancedSignalsIcon />;
  }
};
type CopilotTFunction = TFunction<'common', 'copilot'>;

/**
 * Formats a status timestamp for the tooltip header, or the "pending" copy when there is
 * none. Supplied by the component so this stays hook-free and testable.
 */
export type FormatLastUpdateFn = (lastUpdated?: string) => string;

/** Label for the pipeline-label placeholder in error copy, e.g. "reporting solution". */
export type PipelineLabelFn = (collection: CirrusSourceOption['collection']) => string;

/** Plain status word, e.g. "Paused" — for configs that have no pipeline behind them. */
export type StatusLabelFn = (status: ConfigStatus) => string;
interface BuildCopilotSourceMenuOptionsParams {
  datasourceSubOptions: MultiSelectDropdownItem[];
  knowledgeSourceSubOptions: MultiSelectDropdownItem[];
  /** Cirrus collections read from Alvie's own config lists. */
  cirrusGroups: {
    recommendations: CirrusSourceOption;
    reportingSolutions: CirrusSourceGroup;
    attributionModels: CirrusSourceGroup;
    budgetOptimisers: CirrusSourceGroup;
    biddingSignals: CirrusSourceGroup;
  };
  /** Id of the currently selected option, if any — everything else locks while set. */
  selectedOptionId?: string;
  formatLastUpdate: FormatLastUpdateFn;
  getPipelineLabel: PipelineLabelFn;
  getStatusLabel: StatusLabelFn;
  t: CopilotTFunction;
}

/**
 * The same rich status tooltip the config's own list view shows — last-update header with a
 * badge, then the detailed status block. The list view's trailing "click to view all the
 * details" line is dropped: there is no DAG view to open from inside the picker, and a click
 * here selects the source instead.
 */
const buildStatusTooltipProps = ({
  option,
  formatLastUpdate,
  getPipelineLabel,
  getStatusLabel
}: {
  option: CirrusSourceOption;
  formatLastUpdate: FormatLastUpdateFn;
  getPipelineLabel: PipelineLabelFn;
  getStatusLabel: StatusLabelFn;
}): MultiSelectDropdownItem['tooltipProps'] => {
  if (option.status == null) {
    return undefined;
  }

  // Bidding signals have no pipeline, so there is no status row to read a last update or an
  // error from. The DAG tooltip would claim "pending" and name the config a connector, so
  // show the plain enabled/paused word its own list view shows instead.
  if (option.statusDetail == null) {
    return {
      kind: 'singleline',
      title: getStatusLabel(option.status)
    };
  }
  const {
    lastUpdated,
    errorCode,
    errorMessage,
    targetProjectId
  } = option.statusDetail;
  return {
    kind: 'multiline',
    shouldDelay: true,
    width: 'auto',
    maxWidth: '400px',
    title: <StatusTooltipTitle status={option.status} formattedLastUpdate={formatLastUpdate(lastUpdated)} badgeColor={STATUS_COLOR_MAP[option.status] ?? 'neutral'} shouldShowStatusMessageInTitle={false} />,
    body: <StatusTooltipBody status={option.status} hasLinkToDagView={false} errorCode={errorCode} errorDetails={getErrorCodeDetails(errorCode, errorMessage, targetProjectId, getPipelineLabel(option.collection))} />
  };
};
export const mapCirrusSourceToSubOption = ({
  option,
  isOtherSourceLocked,
  formatLastUpdate,
  getPipelineLabel,
  getStatusLabel,
  t
}: {
  option: CirrusSourceOption;
  isOtherSourceLocked: boolean;
  formatLastUpdate: FormatLastUpdateFn;
  getPipelineLabel: PipelineLabelFn;
  getStatusLabel: StatusLabelFn;
  t: CopilotTFunction;
}): MultiSelectDropdownItem => {
  // Sources the agent cannot answer on yet are listed but not selectable — hiding them
  // would read as the config being missing rather than not supported.
  const isUnsupported = option.supported === false;
  // STATUS_COLOR_MAP has no entry for paused/disabled — the list views show those as text
  // only, but here the dot is the whole signal, so fall back to neutral rather than a blank.
  const statusColor = option.status == null ? undefined : STATUS_COLOR_MAP[option.status] ?? 'neutral';
  return {
    value: option.id,
    label: option.label,
    annotation: option.annotation,
    disabled: isUnsupported || isOtherSourceLocked,
    // `rightIcon` puts the dot just left of the selection checkbox.
    rightIcon: statusColor != null ? <Badge color={statusColor} /> : undefined,
    tooltipProps: isUnsupported ? {
      kind: 'singleline',
      title: t('sourceMenu.comingSoon')
    } : isOtherSourceLocked ? {
      kind: 'singleline',
      title: t('dataAgent.sourceTooltip.otherSourceLocked')
    } : buildStatusTooltipProps({
      option,
      formatLastUpdate,
      getPipelineLabel,
      getStatusLabel
    })
  };
};

/**
 * A Cirrus collection row: a submenu of the client's configs, or a disabled row
 * explaining why there is nothing to pick.
 */
const cirrusCollectionOption = ({
  value,
  label,
  icon,
  group,
  emptyTitle,
  selectedOptionId,
  formatLastUpdate,
  getPipelineLabel,
  getStatusLabel,
  t
}: {
  value: string;
  label: string;
  icon: ReactElement;
  group: CirrusSourceGroup;
  emptyTitle: string;
  selectedOptionId?: string;
  formatLastUpdate: FormatLastUpdateFn;
  getPipelineLabel: PipelineLabelFn;
  getStatusLabel: StatusLabelFn;
  t: CopilotTFunction;
}): MultiSelectDropdownItem => {
  const isEmpty = !group.isLoading && group.options.length === 0;
  return {
    value,
    label,
    leftIcon: icon,
    // Count what can actually be picked, not what is merely listed.
    count: group.options.filter(option => option.supported !== false).length,
    disabled: group.isLoading || isEmpty,
    tooltipProps: isEmpty ? {
      kind: 'singleline',
      title: emptyTitle
    } : undefined,
    subOptions: group.options.map(option => mapCirrusSourceToSubOption({
      option,
      isOtherSourceLocked: selectedOptionId != null && selectedOptionId !== option.id,
      formatLastUpdate,
      getPipelineLabel,
      getStatusLabel,
      t
    }))
  };
};
export const buildKnowledgeOnlySourceMenuOptions = ({
  knowledgeSourceSubOptions,
  t
}: Pick<BuildCopilotSourceMenuOptionsParams, 'knowledgeSourceSubOptions' | 't'>): MultiSelectDropdownItem[] => {
  return [{
    value: COPILOT_KNOWLEDGE_SOURCES_MENU_VALUE,
    label: t('sourceMenu.knowledgeSources'),
    leftIcon: <DocumentationIcon />,
    subOptions: knowledgeSourceSubOptions
  }];
};
export const buildCopilotSourceMenuOptions = ({
  datasourceSubOptions,
  knowledgeSourceSubOptions,
  cirrusGroups,
  selectedOptionId,
  formatLastUpdate,
  getPipelineLabel,
  getStatusLabel,
  t
}: BuildCopilotSourceMenuOptionsParams): MultiSelectDropdownItem[] => {
  const {
    recommendations,
    reportingSolutions,
    attributionModels,
    budgetOptimisers,
    biddingSignals
  } = cirrusGroups;
  const isRecommendationsLocked = selectedOptionId != null && selectedOptionId !== recommendations.id;
  return [{
    value: COPILOT_DATA_SOURCES_MENU_VALUE,
    label: t('sourceMenu.dataSources'),
    leftIcon: <ConnectDataSourcesIcon />,
    subOptions: datasourceSubOptions
  },
  // Recommendations is config-less, so it is a selectable leaf rather than a submenu.
  {
    value: recommendations.id,
    label: t('sourceMenu.recommendations'),
    leftIcon: getCirrusCollectionIcon('event_feed'),
    disabled: isRecommendationsLocked,
    tooltipProps: isRecommendationsLocked ? {
      kind: 'singleline',
      title: t('dataAgent.sourceTooltip.otherSourceLocked')
    } : undefined
  }, cirrusCollectionOption({
    value: 'reporting-solutions',
    label: t('sourceMenu.reportingSolutions'),
    icon: getCirrusCollectionIcon('standard_reports'),
    group: reportingSolutions,
    emptyTitle: t('sourceMenu.empty.reportingSolutions'),
    selectedOptionId,
    formatLastUpdate,
    getPipelineLabel,
    getStatusLabel,
    t
  }), cirrusCollectionOption({
    value: 'attribution-models',
    label: t('sourceMenu.attributionModels'),
    icon: getCirrusCollectionIcon('attribution'),
    group: attributionModels,
    emptyTitle: t('sourceMenu.empty.attributionModels'),
    selectedOptionId,
    formatLastUpdate,
    getPipelineLabel,
    getStatusLabel,
    t
  }), cirrusCollectionOption({
    value: 'budget-optimisers',
    label: t('sourceMenu.budgetOptimisers'),
    icon: getCirrusCollectionIcon('bo'),
    group: budgetOptimisers,
    emptyTitle: t('sourceMenu.empty.budgetOptimisers'),
    selectedOptionId,
    formatLastUpdate,
    getPipelineLabel,
    getStatusLabel,
    t
  }), cirrusCollectionOption({
    value: 'bidding-signals',
    label: t('sourceMenu.biddingSignals'),
    icon: getCirrusCollectionIcon('enhanced_signals'),
    group: biddingSignals,
    emptyTitle: t('sourceMenu.empty.biddingSignals'),
    selectedOptionId,
    formatLastUpdate,
    getPipelineLabel,
    getStatusLabel,
    t
  }), {
    value: COPILOT_KNOWLEDGE_SOURCES_MENU_VALUE,
    label: t('sourceMenu.knowledgeSources'),
    leftIcon: <DocumentationIcon />,
    subOptions: knowledgeSourceSubOptions
  }];
};
export const mapAlvieDatasourceToSubOption = ({
  option,
  platformLabel,
  avatar,
  tooltipProps,
  disabled
}: {
  option: AlvieDatasourceOption;
  platformLabel: string;
  avatar?: ReactElement;
  tooltipProps: MultiSelectDropdownItem['tooltipProps'];
  /** When omitted, falls back to disconnected sources being disabled. */
  disabled?: boolean;
}): MultiSelectDropdownItem => ({
  value: option.id,
  label: option.label,
  annotation: option.subLabel,
  subCategory: platformLabel,
  subCategoryAvatar: avatar,
  tooltipProps,
  disabled: disabled ?? !option.connected
});
export const mapKnowledgeSourceToSubOption = ({
  id,
  name,
  annotation,
  subCategory,
  disabled
}: {
  id: string;
  name: string;
  annotation?: string;
  subCategory?: string;
  disabled?: boolean;
}): MultiSelectDropdownItem => ({
  value: id,
  label: name,
  annotation,
  subCategory,
  disabled: disabled ?? false
});